import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { draftLabel, draftSecondaryText } from "../../Utils/workDrafts";

const WorkListSidebar = ({
  entries = [],
  drafts = [],
  loading = false,
  error = "",
  selectedSlug = "",
  selectedDraftId = "",
  onSelect,
  onSelectDraft,
  onDiscardDraft,
  onNew,
}) => {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const filteredDrafts = useMemo(() => {
    if (!needle) return drafts;
    return drafts.filter((draft) =>
      `${draftLabel(draft)} ${draft.slug || ""} ${draft.updatedBy || ""}`
        .toLowerCase()
        .includes(needle),
    );
  }, [drafts, needle]);
  const filtered = useMemo(() => {
    if (!needle) return entries;
    return entries.filter((entry) =>
      `${entry.slug} ${entry.label}`.toLowerCase().includes(needle),
    );
  }, [entries, needle]);

  return (
    <Card
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: 0,
        boxShadow: "none",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
        <Typography variant="h6" sx={{ color: "#3598b7", fontWeight: 600, mb: 1.5 }}>
          Existing work
        </Typography>
        <Button
          fullWidth
          variant={selectedSlug || selectedDraftId ? "outlined" : "contained"}
          onClick={onNew}
          sx={{ mb: 1.5 }}
        >
          New work
        </Button>
        <TextField
          size="small"
          label="Search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          fullWidth
        />
      </Box>
      {error && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          <Typography
            variant="overline"
            sx={{ px: 2, pt: 1.5, display: "block", color: "rgba(46, 46, 46, 0.65)" }}
          >
            Drafts
          </Typography>
          <List disablePadding>
            {filteredDrafts.map((draft) => (
              <ListItemButton
                key={draft.id}
                selected={draft.id === selectedDraftId}
                onClick={() => onSelectDraft(draft.id)}
                sx={{
                  alignItems: "flex-start",
                  pr: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "rgba(53, 152, 183, 0.12)",
                  },
                }}
              >
                <ListItemText
                  primary={draftLabel(draft)}
                  secondary={draftSecondaryText(draft)}
                  primaryTypographyProps={{
                    sx: { color: "#2e2e2e", fontWeight: 500 },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: "rgba(46, 46, 46, 0.7)" },
                  }}
                />
                <IconButton
                  aria-label={`Discard ${draftLabel(draft)}`}
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDiscardDraft(draft);
                  }}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
            {!filteredDrafts.length && (
              <Typography sx={{ px: 2, py: 1.5, color: "text.secondary" }}>
                No drafts
              </Typography>
            )}
          </List>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: 1.5,
              display: "block",
              color: "rgba(46, 46, 46, 0.65)",
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            Published
          </Typography>
          <List disablePadding>
            {filtered.map((entry) => (
              <ListItemButton
                key={entry.slug}
                selected={entry.slug === selectedSlug && !selectedDraftId}
                onClick={() => onSelect(entry.slug)}
                sx={{
                  alignItems: "flex-start",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(53, 152, 183, 0.12)",
                  },
                }}
              >
                <ListItemText
                  primary={entry.label || entry.slug}
                  secondary={entry.date || entry.slug}
                  primaryTypographyProps={{
                    sx: {
                      color: "#2e2e2e",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: "rgba(46, 46, 46, 0.7)" },
                  }}
                />
              </ListItemButton>
            ))}
            {!filtered.length && !error && (
              <Typography sx={{ px: 2, py: 2, color: "text.secondary" }}>
                No matching work
              </Typography>
            )}
          </List>
        </Box>
      )}
    </Card>
  );
};

export default WorkListSidebar;
